import { useState, useEffect, useCallback, useMemo } from 'react';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { getOrders, getProducts, updateOrderStatus, setOrderTracking, deleteOrder } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ShoppingCart, Eye, Package, CheckCircle, Truck, ExternalLink, Loader2, Trash2, Search } from 'lucide-react';

const CARRIERS = [
  { value: 'colissimo',     label: 'Colissimo (La Poste)',  url: 'https://www.laposte.fr/outils/suivre-vos-envois?code={t}' },
  { value: 'chronopost',    label: 'Chronopost',            url: 'https://www.chronopost.fr/tracking-no-cms/suivi-par-reference?listeNumerosLT={t}' },
  { value: 'ups',           label: 'UPS',                   url: 'https://www.ups.com/track?loc=fr_FR&tracknum={t}' },
  { value: 'mondial_relay', label: 'Mondial Relay',         url: 'https://www.mondialrelay.fr/suivi-de-colis/?NumeroExpedition={t}' },
  { value: 'dpd',           label: 'DPD',                   url: 'https://trace.dpd.fr/fr/trace/{t}' },
  { value: 'fedex',         label: 'FedEx',                 url: 'https://www.fedex.com/fr-fr/tracking.html?tracknumbers={t}' },
];

function getCarrierLabel(value) {
  return CARRIERS.find(c => c.value === value)?.label || value;
}
function getCarrierUrl(value, tracking) {
  const c = CARRIERS.find(c => c.value === value);
  return c ? c.url.replace('{t}', tracking) : '';
}

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [productsMap, setProductsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Filtres
  const [search, setSearch] = useState('');
  const [dateFilter, setDateFilter] = useState('month'); // month | 3months | 6months | all

  // Tracking dialog
  const [trackingOrder, setTrackingOrder] = useState(null);
  const [carrier, setCarrier] = useState('colissimo');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingLoading, setTrackingLoading] = useState(false);

  const fetchOrders = useCallback(async () => {
    try {
      const [ordersRes, productsRes] = await Promise.all([getOrders(), getProducts()]);
      setOrders(ordersRes.data);
      const map = {};
      (productsRes.data || []).forEach(p => { map[p.id] = p; });
      setProductsMap(map);
    } catch (err) {
      if (err.response?.status !== 401) {
        toast.error('Erreur lors du chargement des commandes');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('tapisstore_admin_token')) fetchOrders();
    else setLoading(false);
  }, [fetchOrders]);

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
      toast.success('Statut mis à jour');
      fetchOrders();
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la mise à jour');
    }
  };

  // Résout l'URL d'image d'un article : image stockée en priorité, sinon image du catalogue produits
  const resolveItemImage = (item) => {
    const raw = item.image_url || productsMap[item.product_id]?.images?.[0] || null;
    if (!raw) return null;
    return raw.startsWith('/api/')
      ? `${process.env.REACT_APP_BACKEND_URL}${raw}`
      : raw;
  };

  // Filtrage des commandes
  const filteredOrders = useMemo(() => {
    const now = new Date();
    const cutoffs = { month: 1, '3months': 3, '6months': 6 };
    const months = cutoffs[dateFilter];

    return orders.filter(order => {
      // Filtre date
      if (months) {
        const cutoff = new Date(now);
        cutoff.setMonth(cutoff.getMonth() - months);
        if (new Date(order.created_at) < cutoff) return false;
      }
      // Filtre recherche
      if (search.trim()) {
        const q = search.toLowerCase();
        const name = (order.customer_name || '').toLowerCase();
        const email = (order.customer_email || '').toLowerCase();
        const id = (order.id || '').toLowerCase();
        if (!name.includes(q) && !email.includes(q) && !id.includes(q)) return false;
      }
      return true;
    });
  }, [orders, search, dateFilter]);

  const handleDeleteOrder = async () => {
    if (!deleteConfirmOrder) return;
    setDeleting(true);
    try {
      await deleteOrder(deleteConfirmOrder.id);
      toast.success('Commande supprimée');
      setDeleteConfirmOrder(null);
      fetchOrders();
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleConfirmOrder = async (orderId, customerName) => {
    try {
      await updateOrderStatus(orderId, 'confirmed');
      toast.success(`Commande de ${customerName} confirmée — email envoyé au client`);
      fetchOrders();
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error('Erreur lors de la confirmation');
    }
  };

  const openShippingDialog = (order) => {
    setTrackingOrder(order);
    setCarrier(order.carrier || 'colissimo');
    setTrackingNumber(order.tracking_number || '');
  };

  const handleSetTracking = async () => {
    if (!trackingNumber.trim()) {
      toast.error('Veuillez saisir un numéro de suivi');
      return;
    }
    setTrackingLoading(true);
    try {
      await setOrderTracking(trackingOrder.id, { carrier, tracking_number: trackingNumber.trim() });
      toast.success('Expédition enregistrée — email envoyé au client');
      setTrackingOrder(null);
      setTrackingNumber('');
      fetchOrders();
    } catch (err) {
      const status = err.response?.status;
      if (status !== 401 && status !== 403) toast.error("Erreur lors de l'enregistrement de l'expédition");
    } finally {
      setTrackingLoading(false);
    }
  };

  const previewUrl = carrier && trackingNumber
    ? getCarrierUrl(carrier, trackingNumber)
    : '';

  const viewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending:   'bg-amber-100 text-amber-700',
      confirmed: 'bg-blue-100 text-blue-700',
      preparing: 'bg-orange-100 text-orange-700',
      shipped:   'bg-purple-100 text-purple-700',
      delivered: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700',
    };
    const labels = {
      pending:   'En attente',
      confirmed: 'Acceptée',
      preparing: 'En préparation',
      shipped:   'Expédiée',
      delivered: 'Livrée',
      cancelled: 'Annulée',
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getPaymentBadge = (status) => {
    const styles = {
      pending:  'bg-amber-100 text-amber-700',
      paid:     'bg-green-100 text-green-700',
      failed:   'bg-red-100 text-red-700',
    };
    const labels = {
      pending:  'En attente',
      paid:     'Payé',
      failed:   'Échoué',
      initiated:'En cours',
    };
    return (
      <span className={`text-xs px-2 py-1 rounded-full ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <AdminLayout>
      <div data-testid="admin-orders">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-semibold">Commandes</h1>
          <p className="text-muted-foreground mt-1">Gérez les commandes de vos clients</p>
        </div>

        {/* Barre de recherche + filtres */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Nom, prénom, email, n° commande…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="orders-search-input"
            />
          </div>
          <div className="flex gap-1">
            {[
              { key: 'month',   label: 'Ce mois' },
              { key: '3months', label: '3 mois' },
              { key: '6months', label: '6 mois' },
              { key: 'all',     label: 'Toutes' },
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant={dateFilter === key ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDateFilter(key)}
                className={dateFilter === key ? 'btn-primary' : ''}
                data-testid={`date-filter-${key}`}
              >
                {label}
              </Button>
            ))}
          </div>
          <span className="text-sm text-muted-foreground ml-auto">
            {filteredOrders.length} commande{filteredOrders.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={`skeleton-${i}`} className="h-16 bg-muted rounded animate-pulse" />
            ))}
          </div>
        ) : filteredOrders.length > 0 ? (
          <div className="border border-border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Articles</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Paiement</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Suivi</TableHead>
                  <TableHead className="w-36">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                    <TableCell className="text-sm">{formatDate(order.created_at)}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-sm text-muted-foreground">{order.customer_email}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {order.items && order.items.slice(0, 4).map((item, idx) => {
                          const imgSrc = resolveItemImage(item);
                          return (
                            <div key={idx} className="relative group" title={`${item.product_name} × ${item.quantity}`}>
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={item.product_name}
                                  className="w-10 h-10 rounded object-cover border border-border flex-shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                                />
                              ) : null}
                              <div
                                className="w-10 h-10 bg-muted rounded flex items-center justify-center flex-shrink-0"
                                style={{ display: imgSrc ? 'none' : 'flex' }}
                              >
                                <Package className="w-4 h-4 text-muted-foreground" />
                              </div>
                              {item.quantity > 1 && (
                                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs rounded-full w-4 h-4 flex items-center justify-center font-bold leading-none">
                                  {item.quantity}
                                </span>
                              )}
                            </div>
                          );
                        })}
                        {order.items && order.items.length > 4 && (
                          <span className="text-xs text-muted-foreground ml-1">+{order.items.length - 4}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-semibold text-primary">
                      {order.total.toFixed(2)} €
                    </TableCell>
                    <TableCell>{getPaymentBadge(order.payment_status)}</TableCell>
                    <TableCell>
                      <Select
                        value={order.status}
                        onValueChange={(value) => handleStatusChange(order.id, value)}
                      >
                        <SelectTrigger className="w-36" data-testid={`order-status-${order.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">En attente</SelectItem>
                          <SelectItem value="confirmed">Acceptée</SelectItem>
                          <SelectItem value="preparing">En préparation</SelectItem>
                          <SelectItem value="shipped">Expédiée</SelectItem>
                          <SelectItem value="delivered">Livrée</SelectItem>
                          <SelectItem value="cancelled">Annulée</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {order.tracking_number ? (
                        <div className="text-xs">
                          <p className="font-medium text-foreground">{getCarrierLabel(order.carrier)}</p>
                          <p className="text-muted-foreground font-mono">{order.tracking_number}</p>
                          {order.tracking_url && (
                            <a
                              href={order.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary flex items-center gap-1 mt-0.5 hover:underline"
                            >
                              <ExternalLink className="w-3 h-3" /> Suivre
                            </a>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 flex-wrap">
                        {order.status === 'pending' && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleConfirmOrder(order.id, order.customer_name)}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-2"
                            data-testid={`confirm-order-${order.id}`}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirmer
                          </Button>
                        )}
                        {['confirmed', 'preparing'].includes(order.status) && (
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => openShippingDialog(order)}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs px-2"
                            data-testid={`ship-order-${order.id}`}
                          >
                            <Truck className="w-3 h-3 mr-1" />
                            Expédier
                          </Button>
                        )}
                        {order.status === 'shipped' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openShippingDialog(order)}
                            className="text-xs px-2"
                            data-testid={`edit-tracking-${order.id}`}
                          >
                            <Truck className="w-3 h-3 mr-1" />
                            Suivi
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewDetails(order)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {order.status === 'cancelled' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirmOrder(order)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-order-${order.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-12 bg-muted/30 rounded-lg">
            <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Aucune commande trouvée</p>
            {(search || dateFilter !== 'all') && (
              <p className="text-sm text-muted-foreground mt-1">Essayez d'élargir votre recherche ou la période</p>
            )}
          </div>
        )}

        {/* ── Shipping / Tracking Dialog ── */}
        <Dialog open={!!trackingOrder} onOpenChange={(v) => { if (!v) setTrackingOrder(null); }}>
          <DialogContent className="max-w-md" data-testid="shipping-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading">
                <Truck className="w-5 h-5 text-purple-600" />
                Enregistrer l'expédition
              </DialogTitle>
            </DialogHeader>

            {trackingOrder && (
              <div className="space-y-5 mt-2">
                <div className="bg-muted/50 rounded-lg p-3 text-sm">
                  <p className="font-medium">{trackingOrder.customer_name}</p>
                  <p className="text-muted-foreground">{trackingOrder.shipping_address}</p>
                </div>

                <div className="space-y-2">
                  <Label>Transporteur</Label>
                  <Select value={carrier} onValueChange={setCarrier} data-testid="carrier-select">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CARRIERS.map(c => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tracking-number">Numéro de suivi</Label>
                  <Input
                    id="tracking-number"
                    value={trackingNumber}
                    onChange={(e) => setTrackingNumber(e.target.value)}
                    placeholder="Ex: 1Z999AA10123456784"
                    data-testid="tracking-number-input"
                  />
                </div>

                {/* Preview link */}
                {previewUrl && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                    <p className="text-purple-700 font-medium mb-1">Lien de suivi généré :</p>
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-600 hover:underline break-all flex items-center gap-1"
                      data-testid="tracking-preview-url"
                    >
                      <ExternalLink className="w-3 h-3 shrink-0" />
                      {previewUrl}
                    </a>
                  </div>
                )}

                <p className="text-xs text-muted-foreground">
                  Un email sera envoyé à <strong>{trackingOrder.customer_email}</strong> avec le lien de suivi. La commande passera en statut "Expédiée".
                </p>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setTrackingOrder(null)}>
                Annuler
              </Button>
              <Button
                onClick={handleSetTracking}
                disabled={!trackingNumber.trim() || trackingLoading}
                className="bg-purple-600 hover:bg-purple-700 text-white"
                data-testid="confirm-tracking-btn"
              >
                {trackingLoading
                  ? <Loader2 className="w-4 h-4 animate-spin mr-1" />
                  : <Truck className="w-4 h-4 mr-1" />
                }
                Confirmer l'expédition
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ── Order Details Dialog ── */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-heading">Détails de la commande</DialogTitle>
            </DialogHeader>

            {selectedOrder && (
              <div className="mt-4 space-y-6">
                <div>
                  <h4 className="font-medium mb-2">Informations client</h4>
                  <div className="bg-muted/50 rounded-lg p-4 space-y-1 text-sm">
                    <p><span className="text-muted-foreground">Nom:</span> {selectedOrder.customer_name}</p>
                    <p><span className="text-muted-foreground">Email:</span> {selectedOrder.customer_email}</p>
                    {selectedOrder.customer_phone && (
                      <p><span className="text-muted-foreground">Tél:</span> {selectedOrder.customer_phone}</p>
                    )}
                    <p><span className="text-muted-foreground">Adresse:</span> {selectedOrder.shipping_address}</p>
                  </div>
                </div>

                {/* Tracking info if set */}
                {selectedOrder.tracking_number && (
                  <div>
                    <h4 className="font-medium mb-2">Informations de livraison</h4>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-1 text-sm">
                      <p><span className="text-muted-foreground">Transporteur:</span> <strong>{getCarrierLabel(selectedOrder.carrier)}</strong></p>
                      <p><span className="text-muted-foreground">N° de suivi:</span> <strong className="font-mono">{selectedOrder.tracking_number}</strong></p>
                      {selectedOrder.tracking_url && (
                        <a
                          href={selectedOrder.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-purple-600 hover:underline flex items-center gap-1 mt-1"
                        >
                          <ExternalLink className="w-3 h-3" /> Voir le suivi
                        </a>
                      )}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Articles commandés</h4>
                  <div className="space-y-3">
                    {selectedOrder.items.map((item, index) => {
                      const imgSrc = resolveItemImage(item);
                      return (
                        <div
                          key={item.product_id || `item-${index}`}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {imgSrc ? (
                              <img
                                src={imgSrc}
                                alt={item.product_name}
                                className="w-14 h-14 rounded object-cover border border-border flex-shrink-0"
                                onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                              />
                            ) : null}
                            <div
                              className="w-14 h-14 bg-muted rounded flex items-center justify-center flex-shrink-0"
                              style={{ display: imgSrc ? 'none' : 'flex' }}
                            >
                              <Package className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div>
                              <p className="font-medium text-sm">{item.product_name}</p>
                              <p className="text-xs text-muted-foreground">Qté : {item.quantity}</p>
                            </div>
                          </div>
                          <p className="font-semibold text-sm">
                            {(item.price * item.quantity).toFixed(2)} €
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">Total</span>
                    <span className="text-xl font-semibold text-primary">
                      {selectedOrder.total.toFixed(2)} €
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div>
                    <span className="text-sm text-muted-foreground">Paiement:</span>
                    {' '}{getPaymentBadge(selectedOrder.payment_status)}
                  </div>
                  <div>
                    <span className="text-sm text-muted-foreground">Statut:</span>
                    {' '}{getStatusBadge(selectedOrder.status)}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ── Delete Confirm Dialog ── */}
        <Dialog open={!!deleteConfirmOrder} onOpenChange={(v) => { if (!v) setDeleteConfirmOrder(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading text-destructive flex items-center gap-2">
                <Trash2 className="w-5 h-5" /> Supprimer la commande
              </DialogTitle>
            </DialogHeader>
            {deleteConfirmOrder && (
              <div className="mt-2 space-y-3">
                <p className="text-sm text-muted-foreground">
                  Voulez-vous vraiment supprimer définitivement la commande de{' '}
                  <strong>{deleteConfirmOrder.customer_name}</strong> ({deleteConfirmOrder.id}) ?
                  Cette action est irréversible.
                </p>
              </div>
            )}
            <DialogFooter className="gap-2 mt-4">
              <Button variant="outline" onClick={() => setDeleteConfirmOrder(null)}>Annuler</Button>
              <Button
                variant="destructive"
                onClick={handleDeleteOrder}
                disabled={deleting}
                data-testid="confirm-delete-order-btn"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />}
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
